'use client';

import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import {
  APPLICATION_FORM_TEMPLATES,
  APPLICATION_TEMPLATE_META,
} from '../../constants/application-form-templates';
import type { ApplicationTemplateId } from '../../types/audition.types';

interface TemplateSelectorProps {
  onSelect: (templateId: ApplicationTemplateId) => void;
  className?: string;
}

/**
 * Template selector for choosing a pre-defined application form structure
 */
export function TemplateSelector({
  onSelect,
  className,
}: TemplateSelectorProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('space-y-4', className)}>
      <div>
        <h3 className="text-lg font-semibold">
          <Trans i18nKey="auditions:templateSelector.title" />
        </h3>
        <p className="text-muted-foreground text-sm">
          <Trans i18nKey="auditions:templateSelector.subtitle" />
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {APPLICATION_TEMPLATE_META.map((template) => {
          // Field count comes from the structure apply actually writes, not a
          // stale preview constant — keeps the "N fields" badge honest.
          const fieldCount =
            APPLICATION_FORM_TEMPLATES[template.id]?.fields.length ?? 0;
          return (
            <Card
              key={template.id}
              className="hover:border-primary hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => onSelect(template.id)}
            >
              <CardHeader className="pb-2">
                <div className="bg-primary/10 mb-2 flex h-10 w-10 items-center justify-center rounded-lg">
                  <FileText className="text-primary h-5 w-5" />
                </div>
                <CardTitle className="text-base">{template.name}</CardTitle>
                <CardDescription className="text-xs">
                  {template.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-muted-foreground text-xs">
                  {fieldCount === 0
                    ? t('auditions:templateSelector.emptyTemplate')
                    : t('auditions:templateSelector.fieldCount', {
                        count: fieldCount,
                      })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
